// src/animate/motionPresets.ts
// Centralized, opinionated motion system for entrances + simple background motion.
// Uses global GSAP from CDN (or window.gsap). Types are intentionally loose to keep TS happy.
declare const gsap: any;

/** Safe GSAP getter (no-ops if GSAP not present yet). */
function getGSAP(): any | null {
  try {
    const g =
      (typeof window !== "undefined" ? (window as any).gsap : undefined) ??
      (typeof gsap !== "undefined" ? gsap : undefined);
    return g && typeof g.to === "function" ? g : null;
  } catch {
    return null;
  }
}

/** Optional: respect reduced motion globally */
function prefersReducedMotion(): boolean {
  try { return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false; }
  catch { return false; }
}

/** Base motion defaults (text) */
export const MOTION = {
  dur: 1.0,             // keep in sync with animate.ts baseline
  dist: 24,             // default distance for SLIDES (px)
  ease: "power2.out",
  stagger: 0.1,
  speed: 1,
};

/** Background motion defaults */
export const BG_MOTION = {
  dur: 12,
  ease: "power1.inOut",
  kenburnsDur: 90,
  parallaxEase: "power2.out",
};

type PresetInput = { dur: number; dist: number; delay: number };
type PresetFn = (el: HTMLElement, opt: PresetInput) => any;

// --- Perf helpers: add/remove will-change just while animating ---
function onStartPerf(this: HTMLElement) { this.style.willChange = "opacity, transform, filter"; }
function onDonePerf(this: HTMLElement)  { this.style.willChange = ""; }

// Utility: treat provided dist as a percent for FLOATS
const pct = (n: number | undefined, fallback: number) =>
  typeof n === "number" && isFinite(n) ? n : fallback;

/** --- TEXT PRESETS ------------------------------------------------------ */
const presets: Record<string, PresetFn> = {
  // BUTTERY FADE
  "fade-in": (el, { dur, delay }) => {
    const G = getGSAP(); if (!G) return null;
    return G.fromTo(
      el,
      { autoAlpha: 0, yPercent: 2 },  // subtle lift to avoid flat pop
      {
        autoAlpha: 1, yPercent: 0,
        duration: dur * MOTION.speed,
        ease: MOTION.ease,
        delay,
        immediateRender: false,
        clearProps: "transform,opacity,visibility",
        onStart: onStartPerf.bind(el),
        onComplete: onDonePerf.bind(el),
      }
    );
  },

  "fade-scale": (el, { dur, delay }) => {
    const G = getGSAP(); if (!G) return null;
    return G.fromTo(
      el,
      { autoAlpha: 0, scale: 0.96 },
      {
        autoAlpha: 1, scale: 1,
        duration: dur * MOTION.speed,
        ease: "power4.out",
        delay,
        immediateRender: false,
        clearProps: "transform",
        onStart: onStartPerf.bind(el),
        onComplete: onDonePerf.bind(el),
      }
    );
  },

  "fade-blur": (el, { dur, delay }) => {
    const G = getGSAP(); if (!G) return null;
    return G.fromTo(
      el,
      { autoAlpha: 0, filter: "blur(8px)", y: 10 },
      {
        autoAlpha: 1, filter: "blur(0px)", y: 0,
        duration: dur * MOTION.speed,
        ease: MOTION.ease,
        delay,
        immediateRender: false,
        clearProps: "transform,filter",
        onStart: onStartPerf.bind(el),
        onComplete: onDonePerf.bind(el),
      }
    );
  },

  // SLIDES (px) — crisp directional push
  "slide-up": (el, { dur, dist, delay }) => {
    const G = getGSAP(); if (!G) return null;
    return G.fromTo(
      el,
      { autoAlpha: 0, y:  dist, x: 0, rotation: 0, skewX: 0, skewY: 0 },
      {
        autoAlpha: 1, y: 0, x: 0, rotation: 0, skewX: 0, skewY: 0,
        duration: dur * MOTION.speed,
        ease: MOTION.ease,
        delay, immediateRender: false, clearProps: "transform",
        onStart: onStartPerf.bind(el), onComplete: onDonePerf.bind(el),
      }
    );
  },

  "slide-down": (el, { dur, dist, delay }) => {
    const G = getGSAP(); if (!G) return null;
    return G.fromTo(
      el,
      { autoAlpha: 0, y: -dist, x: 0, rotation: 0, skewX: 0, skewY: 0 },
      {
        autoAlpha: 1, y: 0, x: 0, rotation: 0, skewX: 0, skewY: 0,
        duration: dur * MOTION.speed,
        ease: MOTION.ease,
        delay, immediateRender: false, clearProps: "transform",
        onStart: onStartPerf.bind(el), onComplete: onDonePerf.bind(el),
      }
    );
  },

  "slide-left": (el, { dur, dist, delay }) => {
    const G = getGSAP(); if (!G) return null;
    return G.fromTo(
      el,
      { autoAlpha: 0, x:  dist, y: 0, rotation: 0, skewX: 0, skewY: 0 },
      {
        autoAlpha: 1, x: 0, y: 0, rotation: 0, skewX: 0, skewY: 0,
        duration: dur * MOTION.speed,
        ease: MOTION.ease,
        delay, immediateRender: false, clearProps: "transform",
        onStart: onStartPerf.bind(el), onComplete: onDonePerf.bind(el),
      }
    );
  },

  "slide-right": (el, { dur, dist, delay }) => {
    const G = getGSAP(); if (!G) return null;
    return G.fromTo(
      el,
      { autoAlpha: 0, x: -dist, y: 0, rotation: 0, skewX: 0, skewY: 0 },
      {
        autoAlpha: 1, x: 0, y: 0, rotation: 0, skewX: 0, skewY: 0,
        duration: dur * MOTION.speed,
        ease: MOTION.ease,
        delay, immediateRender: false, clearProps: "transform",
        onStart: onStartPerf.bind(el), onComplete: onDonePerf.bind(el),
      }
    );
  },

  // FLOATS (percent) — cinematic drift, scales with element size
  "float-up": (el, { dur, dist, delay }) => {
    const G = getGSAP(); if (!G) return null;
    const D  = (dur ?? 1.1) * MOTION.speed;
    const YP = pct(dist, 14); // default ~14% lift
    return G.fromTo(
      el,
      { autoAlpha: 0, yPercent: YP, scale: 0.985 },
      {
        autoAlpha: 1, yPercent: 0, scale: 1,
        duration: D,
        ease: "power3.out",
        delay, immediateRender: false, clearProps: "transform",
        onStart: onStartPerf.bind(el), onComplete: onDonePerf.bind(el),
      }
    );
  },

  "float-down": (el, { dur, dist, delay }) => {
    const G = getGSAP(); if (!G) return null;
    const D  = (dur ?? 1.1) * MOTION.speed;
    const YP = -pct(dist, 14); // default ~-14% drop (negative for upward origin)
    return G.fromTo(
      el,
      { autoAlpha: 0, yPercent: YP, scale: 0.985 },
      {
        autoAlpha: 1, yPercent: 0, scale: 1,
        duration: D,
        ease: "power3.out",
        delay, immediateRender: false, clearProps: "transform",
        onStart: onStartPerf.bind(el), onComplete: onDonePerf.bind(el),
      }
    );
  },

  // A more dramatic float with slight blur and depth
  "float-up-cinematic": (el, { dur, dist, delay }) => {
    const G = getGSAP(); if (!G) return null;
    const D  = (dur ?? 1.2) * MOTION.speed;
    const YP = pct(dist, 18); // a bit more lift by default
    return G.fromTo(
      el,
      { autoAlpha: 0, yPercent: YP, scale: 0.965, filter: "blur(1.2px)" },
      {
        autoAlpha: 1, yPercent: 0, scale: 1, filter: "blur(0px)",
        duration: D,
        ease: "power3.out",
        delay, immediateRender: false, clearProps: "transform,filter",
        onStart: onStartPerf.bind(el), onComplete: onDonePerf.bind(el),
      }
    );
  },

  "float-up-hero": (el, { dur, dist, delay }) => {
    const G = getGSAP(); if (!G) return null;
    const D  = (dur ?? 1.5) * MOTION.speed;
    const YP = pct(dist, 20); // hero-level lift
    return G.fromTo(
      el,
      { autoAlpha: 0, yPercent: YP, scale: 0.98 },
      {
        autoAlpha: 1, yPercent: 0, scale: 1,
        duration: D,
        ease: "sine.out",
        delay, immediateRender: false, clearProps: "transform",
        onStart: onStartPerf.bind(el), onComplete: onDonePerf.bind(el),
      }
    );
  },

  // For lines/dividers
  "grow-x": (el, { dur, delay }) => {
    const G = getGSAP(); if (!G) return null;
    return G.fromTo(
      el,
      { autoAlpha: 0, scaleX: 0, transformOrigin: "left center" },
      {
        autoAlpha: 1, scaleX: 1,
        duration: dur * MOTION.speed,
        ease: MOTION.ease,
        delay,
        immediateRender: false,
        clearProps: "transform,transformOrigin",
        onStart: onStartPerf.bind(el),
        onComplete: onDonePerf.bind(el),
      }
    );
  },
};

export type PresetName = keyof typeof presets;

/** Resolve preset name with tolerant matching. */
export function resolvePreset(token: string): PresetFn {
  const t = (token || "").toLowerCase().trim();
  if (presets[t]) return presets[t];
  if (t.includes("cinematic"))     return presets["float-up-cinematic"];
  if (t.includes("float-up-hero")) return presets["float-up-hero"];
  if (t.includes("float-up"))      return presets["float-up"];
  if (t.includes("float-down"))    return presets["float-down"];
  if (t.includes("fade-blur"))     return presets["fade-blur"];
  if (t.includes("slide-left"))    return presets["slide-left"];
  if (t.includes("slide-right"))   return presets["slide-right"];
  if (t.includes("slide-down"))    return presets["slide-down"];
  if (t.includes("slide-up"))      return presets["slide-up"];
  if (t.includes("fade-scale"))    return presets["fade-scale"];
  return presets["fade-in"];
}

/** Apply one text preset to a single element. */
export function applyPreset(
  el: HTMLElement,
  token: string,
  opt: Partial<PresetInput> = {}
): any {
  if (prefersReducedMotion()) {
    // accessibility – make visible without motion
    el.style.opacity = "1";
    el.style.transform = "none";
    el.style.visibility = "visible";
    el.setAttribute("data-animated", "true");
    return null;
  }
  const preset = resolvePreset(token);
  const dur   = opt.dur   ?? MOTION.dur;
  const dist  = opt.dist  ?? MOTION.dist;  // NOTE: slides read px; floats read % via pct()
  const delay = opt.delay ?? 0;

  const tween = preset(el, { dur, dist, delay });

  try {
    const prev = tween?.eventCallback?.("onComplete");
    tween?.eventCallback?.("onComplete", () => {
      try { if (typeof prev === "function") prev(); } catch {}
      el.setAttribute("data-animated", "true");
      el.style.visibility = "visible";
      el.style.opacity = "1";
      onDonePerf.call(el);
    });
  } catch {}

  return tween;
}

/** --- GLOBAL SPEED CONTROL ---------------------------------------------- */
export function setMotionSpeed(multiplier: number): void {
  const m = Math.max(0.25, Math.min(3, Number(multiplier) || 1));
  MOTION.speed = m;
}

/** --- GROUP / STAGGER HELPERS ------------------------------------------- */
type GroupOpts = { delay?: number; stagger?: number; dist?: number; dur?: number; from?: number | "start" | "center" | "end"; };

export function animateGroup(
  elements: ArrayLike<HTMLElement>,
  token: string,
  opts: GroupOpts = {}
): any {
  const list = Array.from(elements || []);
  if (!list.length) return null;
  if (prefersReducedMotion()) {
    list.forEach((el) => { el.style.opacity = "1"; el.style.transform = "none"; el.style.visibility = "visible"; });
    return null;
  }

  const G = getGSAP(); if (!G) return null;

  const dur     = (opts.dur   ?? MOTION.dur) * MOTION.speed;
  const dist    =  opts.dist  ?? MOTION.dist;
  const delay   =  opts.delay ?? 0;
  const stagger =  opts.stagger ?? MOTION.stagger;

  const preset = resolvePreset(token);
  const tl = G.timeline({ delay });
  list.forEach((el, i) => {
    const extraDelay = parseFloat(el.getAttribute("data-anim-delay") || "0") || 0;
    const elDur  = parseFloat(el.getAttribute("data-anim-dur")  || "") || dur;
    const elDist = parseFloat(el.getAttribute("data-anim-dist") || "") || dist;
    tl.add(() => preset(el as HTMLElement, { dur: elDur, dist: elDist, delay: extraDelay }), i === 0 ? 0 : `+=${stagger}`);
  });
  return tl;
}

/** --- BACKGROUND / IMAGE PRESETS ---------------------------------------- */
type BgPanOpts = { from?: string; to?: string; dur?: number; ease?: string; delay?: number; };
type BgKenburnsOpts = { fromScale?: number; toScale?: number; dur?: number; ease?: string; delay?: number; origin?: string; };
type BgParallaxOpts = { y?: number; dur?: number; ease?: string; delay?: number; };

export const BG = {
  pan(el: HTMLElement, opts: BgPanOpts = {}) {
    const G = getGSAP(); if (!G) return null;
    const from  = opts.from ?? "50% 50%";
    const to    = opts.to   ?? "50% 50%";
    const dur   = (opts.dur ?? BG_MOTION.dur) * MOTION.speed;
    const ease  = opts.ease ?? BG_MOTION.ease;
    const delay = opts.delay ?? 0;

    G.set(el, { backgroundPosition: from });
    return G.to(el, { backgroundPosition: to, duration: dur, ease, delay });
  },

  kenburns(el: HTMLElement, opts: BgKenburnsOpts = {}) {
    const G = getGSAP(); if (!G) return null;
    const fromScale = opts.fromScale ?? 1.12;
    const toScale   = opts.toScale   ?? 1.0;
    const origin    = opts.origin    ?? "50% 50%";
    const dur       = (opts.dur ?? BG_MOTION.kenburnsDur) * MOTION.speed;
    const ease      = opts.ease ?? "power1.out";
    const delay     = opts.delay ?? 0;

    G.set(el, { transformOrigin: origin, scale: fromScale });
    return G.to(el, { scale: toScale, duration: dur, ease, delay });
  },

  parallaxY(el: HTMLElement, opts: BgParallaxOpts = {}) {
    const G = getGSAP(); if (!G) return null;
    const y     = opts.y ?? 40;
    const dur   = (opts.dur ?? 2.0) * MOTION.speed;
    const ease  = opts.ease ?? BG_MOTION.parallaxEase;
    const delay = opts.delay ?? 0;

    return G.fromTo(el, { y }, { y: 0, duration: dur, ease, delay, clearProps: "transform" });
  },
};

export const background = { pan: BG.pan, kenburns: BG.kenburns, parallaxY: BG.parallaxY };

/** --- LITTLE UTILITIES --------------------------------------------------- */
export function readNumberAttr(el: Element, name: string, fallback = 0): number {
  const v = parseFloat(el.getAttribute(name) || "");
  return Number.isFinite(v) ? v : fallback;
}